import {parse} from 'stacktrace-parser'

/**
 * These errors occur in firefox and chrome, respectively, when the resize observer
 * is not able to deliver all its notifactions within a single animation frame.
 * It doesn't materially affect the application, so we should ignore them to avoid
 * polluting sentry / datadog.
 * It does point to our notebooks being painted somewhat inefficiently
 */
const RESIZE_OBSERVER_ERRORS = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
]

/**
 * Firefox (and perhaps other browsers) will ocassionally return a null
 * value for the error property on an error event.
 * It is unclear why this would happen, but it leads to some confusing error reporting
 * in Sentry
 *
 * @link https://developer.mozilla.org/en-US/docs/Web/API/GlobalEventHandlers/onerror
 */
const errorFallback = {
  name: 'NullErrorEvent',
  stack: '',
}

const errorMetadata: Record<string, string> = {}

function setErrorMetadata(key: string, value: string) {
  errorMetadata[key] = value
}

/**
 * The domains of iframe on the page are captured in the ancestorOrigins property on the
 * location object.
 * However, not all browsers expose the ancestorOrigins property of an iframe, e.g. FireFox.
 * In order to determine where the error we are capturing is coming from, we can grab the
 * document.referrer property.
 *
 * Using that property exposes the entire URL, including path information, which we do not want
 * to capture. So, we convert the referrer to a URL object and pass only the domain along along with
 * the other error information.
 * @returns The parsed domain or 'unknown'
 */
function getIframeDomain() {
  const originUrls = window.location.ancestorOrigins
  let origin = 'unknown'

  if (!originUrls || originUrls.length === 0) {
    return origin
  }

  const rawUrl = originUrls?.[0] ?? document.referrer

  if (!rawUrl) {
    return origin
  }

  const url = new URL(rawUrl)
  // Extract only the domain from the parsed URL. We do this to avoid
  // leaking potentially private repo information to our logging tools.
  origin = url.origin

  return origin
}

/**
 * Many errors are emitted from browser extensions, e.g. users
 * with TexAllTheThings, which attempts to load a version of mathjax.
 * That extension doesn't handle load failures and results in errors
 * being reported to us that we:
 * * cannot control
 * * do not affect the rendering of the notebook
 * So, we ignore them when detected.
 */
function isBrowserExtension(filename = '', stack = '') {
  return (
    filename.includes('chrome-extension://') ||
    stack.includes('chrome-extension://') ||
    filename.includes('moz-extension://') ||
    stack.includes('moz-extension://') ||
    filename.includes('safari-extension://') ||
    stack.includes('safari-extension://') ||
    filename.includes('webkit-masked-url://') ||
    stack.includes('webkit-masked-url://') ||
    filename.includes('extension://') ||
    stack.includes('extension://')
  )
}

// Capture errors coming from the dev console itself
function isDevConsoleError(stack = '') {
  return stack.length > 0 && stack.includes('anonymous')
}

// In the context of JS served in an iframe on another domain,
// script errors are unhelpful errors that are emitted from
// cross-origin scripts we don't control.
function isScriptError(message = '') {
  return message.length > 0 && message === 'Script error.'
}

function reportError(event: ErrorEvent) {
  event.preventDefault()

  const {message, filename, lineno, error} = event
  const safeError = error || errorFallback
  const {stack, name} = safeError

  const preprocessedStack = parse(stack)
  const stacktrace = preprocessedStack
    .map(frame => ({
      filename: frame.file || '',
      function: String(frame.methodName),
      lineno: (frame.lineNumber || 0).toString(),
      colno: (frame.column || 0).toString(),
    }))
    .reverse()
  const exceptionDetail = [
    {
      type: name,
      value: message,
      stacktrace,
    },
  ]

  const params = new URLSearchParams(window.location.search)
  const isIframe = window.parent !== window
  const iframeSrc = getIframeDomain()
  let errorType = name

  if (
    RESIZE_OBSERVER_ERRORS.includes(message) ||
    isBrowserExtension(filename, stack) ||
    isDevConsoleError(stack) ||
    isScriptError(message)
  ) {
    errorType = 'ClientAggregateError'
  }

  const errorUrl = '/_errors'
  const url = new URL(window.location.href)
  const data = {
    error: message,
    stack,
    filename,
    lineno,
    browser: params.get('browser'),
    commit: params.get('commit'),
    repository_id: params.get('repository_id'),
    repository_type: params.get('repository_type'),
    version: params.get('version'),
    domain: url.hostname,
    path: url.pathname,
    origin: errorMetadata.origin,
    is_iframe: isIframe,
    iframe_src: iframeSrc,
    origin_message: errorMetadata.origin_message,
    release: 'notebooks-js@0.0.1',
    platform: 'javascript',
    error_type: errorType,
    exception_detail: exceptionDetail,
  }

  const postObject = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  }

  fetch(errorUrl, postObject)

  return {postObject, data}
}

window.addEventListener('error', reportError)

export {setErrorMetadata, reportError}
